import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useStepperStore from '@store/stepperStore';
import useSiteLeadStore from '@store/siteLeadStore';
import { SUPERVISOR_ALLOWED_CLASS } from '@config/constant';
import { useComponentLevelAccessCheck } from '../../../authGuard/hooks/useComponentLevelAccessCheck';
import {
	recoverPendingSiteVisitRecordingUpload,
	recoverSiteVisitRecordingSession,
} from '@services/siteVisitRecording/siteVisitRecordingEngine';
import { findInterruptedSiteVisitRecordingSession } from '@services/siteVisitRecording/siteVisitRecordingPersistence';
import { useSiteVisitRecordingEligibility } from './useSiteVisitRecordingEligibility';

const FORM_RECORDING_PATHS = ['/add-new-site', '/add-new-site-ss'];

function isFormRecordingRoute(pathname: string): boolean {
	return FORM_RECORDING_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/** Do not resume visit-form audio on the Visit Recorded success step (BDE Feedback uses a fresh session). */
function getMaxVisitFormRecordingStep(pathname: string): number {
	return pathname.startsWith('/add-new-site-ss') ? 1 : 2;
}

/**
 * After reload during an in-progress recording, resume from IndexedDB chunks (form/draft flow only).
 * If there is no in-progress session, instead restore a finished-but-not-yet-uploaded recording
 * (e.g. Submit failed before reload) as a pending upload so it is retried rather than discarded.
 * Neither case is cleared here — only starting a brand-new recording discards prior site data.
 */
export function useRecoverSiteVisitRecordingOnFormLoad(): boolean {
	const { recordingEnabled, siteId } = useSiteVisitRecordingEligibility();
	const hasVisitRecordingConsent = useSiteLeadStore((s) => s.hasVisitRecordingConsent);
	const { pathname } = useLocation();
	const [recoveryComplete, setRecoveryComplete] = useState(false);

	const { siteDetails } = useSiteLeadStore();
	const isKAEGlueFields = useComponentLevelAccessCheck('kae-glue-fields');
	const isLC = isKAEGlueFields && SUPERVISOR_ALLOWED_CLASS.includes(siteDetails?.contractorclass || '');
	const successStep = isLC ? 4 : 3;

	useEffect(() => {
		let cancelled = false;

		const run = async (): Promise<void> => {
			if (!isFormRecordingRoute(pathname)) {
				return;
			}

			// Consent false: never restore/upload — wipe leftover chunks from a prior consent visit.
			if (!hasVisitRecordingConsent) {
				const { discardActiveSiteVisitRecording } = await import(
					'@services/siteVisitRecording/siteVisitRecordingEngine'
				);
				await discardActiveSiteVisitRecording();
				return;
			}

			if (!recordingEnabled || siteId == null) {
				return;
			}
			const currentStep = useStepperStore.getState().currentStep;

			// Check what kind of session is waiting in the DB before we try to recover it
			const interruptedSession = await findInterruptedSiteVisitRecordingSession(siteId);

			if (interruptedSession) {
				const isBdeFeedback = interruptedSession.uploadOnStop;

				if (isBdeFeedback) {
					// It's a BDE Feedback recording. We should recover it and jump to the success step.
					const resumed = await recoverSiteVisitRecordingSession(siteId);
					if (resumed) {
						useStepperStore.getState().setCurrentStep(successStep);
					}
				} else {
					// It's a normal form recording. Only recover if we are on a valid form step.
					if (currentStep <= getMaxVisitFormRecordingStep(pathname)) {
						await recoverSiteVisitRecordingSession(siteId);
					}
				}
			} else {
				if (currentStep <= getMaxVisitFormRecordingStep(pathname)) {
					await recoverPendingSiteVisitRecordingUpload(siteId);
				}
			}
		};

		run().finally(() => {
			if (!cancelled) {
				setRecoveryComplete(true);
			}
		});

		return (): void => {
			cancelled = true;
		};
	}, [recordingEnabled, hasVisitRecordingConsent, siteId, pathname, successStep]);

	return recoveryComplete;
}
